import { Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms'
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog'
import { Editor } from 'primeng/editor'

@Component({
  selector: 'jml-lib-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true
    }
  ]
})
export class RichTextEditorComponent implements OnInit, ControlValueAccessor {
  @ViewChild('editor') editorComponent: Editor

  @Input() text: string
  @Input() saveButtonLabel: string
  @Input() mandatory: boolean
  @Input() height: number = 10 // default height in rows
  @Input() readOnly: boolean = false
  @Input() showSaveButton: boolean = true
  @Input() formControl: FormControl

  calledFromHTML = true

  // Need to disable 'matchVisual' to prevent additional unwanted p-tags
  // https://github.com/quilljs/quill/issues/1379
  quillModules = {
    clipboard: {
      matchVisual: false
    }
  }

  @Output() editedText = new EventEmitter<string>()
  @Output() textChange = new EventEmitter<string>()
  @Output() blur = new EventEmitter<void>()
  @Output() focus = new EventEmitter<void>()

  // ControlValueAccessor implementation
  private onChange: (value: string) => void = () => {}
  private onTouched: () => void = () => {}

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {}

  ngOnInit(): void {
    // support for both direct input and dialog config
    if (!this.text && this.config?.data) {
      this.calledFromHTML = false
      this.text = this.config.data['text']
      this.mandatory = this.config.data['mandatory']
      this.saveButtonLabel = this.config.data['saveButtonLabel']
      this.readOnly = this.config.data['readOnly']
    }

    if (!this.saveButtonLabel) {
      this.saveButtonLabel = 'Speichern'
    }

    // if formControl is provided, use its value
    if (this.formControl) {
      this.text = this.formControl.value

      // subscribe to formControl changes
      this.formControl.valueChanges.subscribe((value) => {
        if (value !== this.text) {
          this.text = value
        }
      })
    }
  }

  save() {
    // send edited code back to "caller"
    // in case of Entity dialog this component is called from html
    // in all other cases it is called via primeng dynamic dialog
    if (this.calledFromHTML) {
      this.editedText.emit(this.text)
    } else {
      this.ref.close(this.text)
    }
  }

  // Handle text changes
  onTextChanged(event) {
    const value = event.htmlValue
    this.text = value

    // Update form control if provided
    if (this.formControl && this.formControl.value !== value) {
      this.formControl.setValue(value, { emitEvent: true }) // Wichtig: emitEvent auf true setzen
    }

    // Notify about changes
    this.onChange(value)
    this.textChange.emit(value)
  }

  // Handle blur events
  onBlur() {
    this.onTouched()
    this.blur.emit()
  }

  onFocus() {
    this.focus.emit()
  }

  // ControlValueAccessor methods
  writeValue(value: string): void {
    this.text = value
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.readOnly = isDisabled
  }
}
